import {useState, useEffect} from 'react';
import {useRouter} from 'next/router';
import Head from 'next/head';
import {DefaultSeo, OrganizationJsonLd, SiteLinksSearchBoxJsonLd} from 'next-seo';
import TNHContext from '@contexts/TNHContext';
import ConfigService from '@services/config.service';
import NewsService from '@services/news.service';
import BusinessService from '@services/business.service';
import AppConfigService from '@services/app-config.service';

import '../styles/antd.css';
import '../styles/custom.css';
import {getSeoOnPage} from '@helper/seo/seo';
import {enrichSeoForLocale, getSiteUrl} from '@helper/seo/site';
import 'video-react/styles/scss/video-react.scss';
import Footer from '@components/common/Layout/Footer';
import Header from '@components/common/Layout/Header';

function MyApp({Component, pageProps, defaultSeo = {}, globalData = {}}) {
    const router = useRouter();
    const {locale} = useRouter();
    const [email, setEmail] = useState('');
    const [hotline, setHotline] = useState('');
    const [facebook, setFacebook] = useState('');
    const [youtube, setYoutube] = useState('');
    const [tiktok, setTiktok] = useState('');
    const [pressTalkTnhId, setPressTalkTnhId] = useState('');
    const [companyId, setCompanyId] = useState('31');
    const [promotionId, setPromotionId] = useState('25');
    const [newsCategories, setNewsCategories] = useState([]);
    const [businessData, setBusinessData] = useState([]);
    const [menuTop, setMenuTop] = useState(globalData.menuTop || []);
    const [menuFooter, setMenuFooter] = useState(globalData.menuFooter || []);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const localizedDefaultSeo = enrichSeoForLocale(defaultSeo, {locale, asPath: router.asPath});
    const localizedPageProps = pageProps?.seoPage
        ? {
              ...pageProps,
              seoPage: enrichSeoForLocale(pageProps.seoPage, {locale, asPath: router.asPath}),
          }
        : pageProps;
    const siteUrl = getSiteUrl();
    const socialProfiles = [facebook, youtube, tiktok].filter(Boolean);

    // GA Tracking
    useEffect(() => {
        const handleRouteChange = url => {
            // Track pageview
            if (typeof window.gtag === 'function') {
                window.gtag('config', 'G-Q7S54K5K0E', {
                    page_path: url,
                });
            }
        };

        router.events.on('routeChangeComplete', handleRouteChange);

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [router.events]);

    useEffect(() => {
        if (globalData.locale !== locale) return;

        if (globalData.menuTop?.length) setMenuTop(globalData.menuTop);
        if (globalData.menuFooter?.length) setMenuFooter(globalData.menuFooter);
    }, [globalData, locale]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const configService = new ConfigService();
                const response = await configService.postAppConfig(
                    'WEB_EMAIL,WEB_HOTLINE,WEB_FACEBOOK,WEB_YOUTUBE,WEB_TIKTOK,WEB_PRESSTALKTNH_ID',
                );
                setEmail(response.WEB_EMAIL || '');
                setHotline(response.WEB_HOTLINE || '');
                setFacebook(response.WEB_FACEBOOK || '');
                setYoutube(response.WEB_YOUTUBE || '');
                setTiktok(response.WEB_TIKTOK || '');
                setPressTalkTnhId(response.WEB_PRESSTALKTNH_ID || '');
            } catch (error) {
                console.error('Failed to fetch app config:', error);
            }
        };

        fetchConfig();
    }, []);

    useEffect(() => {
        const newsService = new NewsService();
        newsService
            .getNewsCategoryList({locale})
            .then(response => {
                setNewsCategories(response.categoryList);
            })
            .catch(error => {
                console.error('Error fetching news categories:', error);
            });
    }, [locale]);

    useEffect(() => {
        const businessService = new BusinessService();
        businessService
            .getBusinessHome({locale})
            .then(response => {
                setBusinessData(response);
            })
            .catch(error => {
                console.error('Error fetching business data:', error);
            });
    }, [locale]);

    useEffect(() => {
        const fetchWebConfig = async () => {
            if (globalData.locale === locale && (globalData.menuTop?.length || globalData.menuFooter?.length)) {
                return;
            }

            try {
                const configService = new ConfigService();
                const response = await configService.getConfigWeb({locale});
                setMenuTop(response.menu_top || []);
                setMenuFooter(response.menu_footer || []);
            } catch (error) {
                console.error('Failed to fetch web config:', error);
            }
        };

        fetchWebConfig();
    }, [globalData, locale]);

    const noLayout = Component.noLayout || false;
    return (
        <>
            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
                <meta name="theme-color" content="#006940" />
            </Head>
            <DefaultSeo {...localizedDefaultSeo} />
            <OrganizationJsonLd
                type="MedicalOrganization"
                id={`${siteUrl}/#organization`}
                name="CTCP Tập đoàn Bệnh viện TNH"
                legalName="CTCP Tập đoàn Bệnh viện TNH"
                url={siteUrl}
                logo={`${siteUrl}/bw_image/logo-tnh-1.png`}
                sameAs={socialProfiles}
                contactPoints={
                    email || hotline
                        ? [
                              {
                                  contactType: 'customer service',
                                  telephone: hotline,
                                  email,
                                  areaServed: 'VN',
                                  availableLanguage: ['Vietnamese', 'English'],
                              },
                          ]
                        : undefined
                }
            />
            <SiteLinksSearchBoxJsonLd
                url={siteUrl}
                potentialActions={[
                    {
                        target: `${siteUrl}/tim-kiem?filterType=0&filterValue`,
                        queryInput: 'search_term_string',
                    },
                ]}
            />
            <TNHContext.Provider
                value={{
                    email,
                    setEmail,
                    hotline,
                    setHotline,
                    facebook,
                    setFacebook,
                    youtube,
                    setYoutube,
                    tiktok,
                    setTiktok,
                    pressTalkTnhId,
                    setPressTalkTnhId,
                    promotionId,
                    setPromotionId,
                    companyId,
                    setCompanyId,
                    newsCategories,
                    setNewsCategories,
                    businessData,
                    setBusinessData,
                    menuTop,
                    setMenuTop,
                    menuFooter,
                    setMenuFooter,
                    selectedDoctor,
                    setSelectedDoctor,
                }}>
                {!noLayout && <a className="skip-link" href="#main-content">Bỏ qua đến nội dung chính</a>}
                {!noLayout && <Header />}
                <main id="main-content">
                    <Component {...localizedPageProps} defaultSeo={localizedDefaultSeo} />
                </main>
                {!noLayout && <Footer />}
            </TNHContext.Provider>
        </>
    );
}
MyApp.getInitialProps = async appContext => {
    try {
        // Next.js App getInitialProps nhận {Component, ctx}
        const {Component, ctx} = appContext || {};
        const safeCtx = ctx || {};

        const fallbackSiteUrl = getSiteUrl();
        let defaultSeo = {
            title: 'CTCP Tập đoàn Bệnh viện TNH',
            description: 'CTCP Tập đoàn Bệnh viện TNH - Chuyên cung cấp dịch vụ y tế uy tín, chất lượng.',
            canonical: fallbackSiteUrl,
            additionalMetaTags: [
                {
                    name: 'keywords',
                    content: 'CTCP Tập đoàn Bệnh viện TNH',
                },
            ],
            openGraph: {
                type: 'website',
                locale: 'vi_VN',
                url: fallbackSiteUrl,
                title: 'CTCP Tập đoàn Bệnh viện TNH',
                description: 'CTCP Tập đoàn Bệnh viện TNH - Chuyên cung cấp dịch vụ y tế uy tín, chất lượng.',
                siteName: 'CTCP Tập đoàn Bệnh viện TNH',
                images: [
                    {
                        url: `${fallbackSiteUrl}/bw_image/logo-tnh-1.png`,
                        width: 215,
                        height: 144,
                        alt: 'CTCP Tập đoàn Bệnh viện TNH',
                    },
                ],
            },
            twitter: {
                cardType: 'summary_large_image',
                site: '@tnhhospital',
            },
        };

        // Fetch SEO config từ API
        try {
            const appConfigService = new AppConfigService(safeCtx);
            const seoConfig = await appConfigService.getAppConfig({page: 'SEO'});

            if (seoConfig && Object.keys(seoConfig).length > 0) {
                // Sử dụng getSeoOnPage để tạo SEO object từ config
                defaultSeo = getSeoOnPage('DEFAULT', seoConfig);
            }
        } catch (error) {
            console.error('Failed to fetch SEO config, using default:', error);
        }

        let pageProps = {};
        if (Component?.getInitialProps) {
            pageProps = await Component.getInitialProps(safeCtx);
        }

        let globalData = {
            locale: safeCtx.locale || 'vn',
            menuTop: [],
            menuFooter: [],
        };

        try {
            const configService = new ConfigService(safeCtx);
            const webConfig = await configService.getConfigWeb({locale: globalData.locale});
            globalData = {
                ...globalData,
                menuTop: webConfig?.menu_top || [],
                menuFooter: webConfig?.menu_footer || [],
            };
        } catch (error) {
            console.error('Failed to fetch global navigation:', error);
        }

        return {pageProps, defaultSeo, globalData};
    } catch (error) {
        console.error('_app error', error);
        return {pageProps: {}, defaultSeo: {}, globalData: {menuTop: [], menuFooter: []}};
    }
};

export default MyApp;
